/**
 * Environment Configuration
 * 
 * Centralized configuration with safe defaults and Vite env variable support
 */

// Helper to safely parse boolean env vars
const parseBoolean = (value: string | undefined, defaultValue: boolean): boolean => {
  if (value === undefined) return defaultValue;
  return value === 'true' || value === '1';
};

// Helper to safely parse number env vars
const parseNumber = (value: string | undefined, defaultValue: number): number => {
  if (value === undefined) return defaultValue;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? defaultValue : parsed;
};

// F-24: warn loudly if VITE_API_URL is missing in production builds
const rawApiUrl = import.meta.env.VITE_API_URL;
if (!rawApiUrl && import.meta.env.PROD) {
  console.error('[env] VITE_API_URL is not set. API calls will fail in production.');
}
// Empty string = use Vite proxy (dev mode, no CORS). Fallback only when undefined.
const API_BASE_URL = rawApiUrl !== undefined ? rawApiUrl : 'http://localhost:8080';

// F-21: derive WS URL from API URL — always wss:// when API is https://
const deriveWsUrl = (apiUrl: string): string => {
  if (import.meta.env.VITE_WS_URL) return import.meta.env.VITE_WS_URL;
  return apiUrl.replace(/^https:\/\//, 'wss://').replace(/^http:\/\//, 'ws://');
};

export const ENV = {
  // API Configuration
  API_BASE_URL,

  // F-20: Mock data only allowed in non-production builds. VITE_USE_MOCK_DATA env var
  // is intentionally ignored in production — it cannot bypass the real backend.
  USE_MOCK_DATA: import.meta.env.PROD ? false : parseBoolean(import.meta.env.VITE_USE_MOCK_DATA, false),

  // WebSocket URL — wss:// in production, ws:// in dev (F-21)
  WS_URL: deriveWsUrl(API_BASE_URL),
  
  // API Timeout (milliseconds)
  API_TIMEOUT: parseNumber(import.meta.env.VITE_API_TIMEOUT, 30000),
  
  // WebSocket Reconnect Settings
  WS_MAX_RECONNECT_ATTEMPTS: parseNumber(import.meta.env.VITE_WS_MAX_RECONNECT_ATTEMPTS, 5),
  WS_RECONNECT_INTERVAL: parseNumber(import.meta.env.VITE_WS_RECONNECT_INTERVAL, 3000),
  
  // Feature Flags
  ENABLE_WEBSOCKETS: parseBoolean(import.meta.env.VITE_ENABLE_WEBSOCKETS, false),
  ENABLE_OPTIMISTIC_UPDATES: parseBoolean(import.meta.env.VITE_ENABLE_OPTIMISTIC_UPDATES, false),
  
  // Debug Mode
  DEBUG: parseBoolean(import.meta.env.VITE_DEBUG, true),
  
  // OAuth Configuration (optional)
  GOOGLE_CLIENT_ID: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
  GITHUB_CLIENT_ID: import.meta.env.VITE_GITHUB_CLIENT_ID || '',
  
  // Error Tracking (optional)
  SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN || '',
  SENTRY_ENVIRONMENT: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development',

  // Product Analytics — PostHog EU (optional, #50)
  POSTHOG_KEY: (import.meta.env.VITE_POSTHOG_KEY || '') as string,
  POSTHOG_HOST: (import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com') as string,
} as const;

// Type-safe environment checkers
export const isProduction = () => !ENV.DEBUG;
export const isDevelopment = () => ENV.DEBUG;
export const useMockData = () => ENV.USE_MOCK_DATA;
export const isWebSocketEnabled = () => ENV.ENABLE_WEBSOCKETS;
export const isOptimisticUpdatesEnabled = () => ENV.ENABLE_OPTIMISTIC_UPDATES;